import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaIndustry,
  FaTasks,
  FaBox,
  FaCog,
  FaUserCircle,
  FaCalendarAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
 
const STEPS = [
  { key: "type", label: "Request Type", icon: <FaCog className="h-5 w-5" /> },
  {
    key: "source",
    label: "Source / Product",
    icon: <FaBox className="h-5 w-5" />,
  },
  {
    key: "bom",
    label: "BOM & RM Calc",
    icon: <FaIndustry className="h-5 w-5" />,
  },
  {
    key: "assign",
    label: "Assign & Date",
    icon: <FaUserCircle className="h-5 w-5" />,
  },
  {
    key: "review",
    label: "Review & Submit",
    icon: <FaTasks className="h-5 w-5" />,
  },
];

export default function ProductionRequestWizard() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [requestType, setRequestType] = useState("SALES_ORDER");
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSalesOrders, setSelectedSalesOrders] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantityRequired, setQuantityRequired] = useState("");

  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");

  const [boms, setBoms] = useState([]);
  const [selectedBOMId, setSelectedBOMId] = useState("");
  const [selectedBOM, setSelectedBOM] = useState(null);
  const [calculatedRMs, setCalculatedRMs] = useState([]);
  const [stockStatus, setStockStatus] = useState([]);
  const [hasLowStock, setHasLowStock] = useState(false);

  const [assignedTo, setAssignedTo] = useState("");
  const [users, setUsers] = useState([]);
  const [requiredDate, setRequiredDate] = useState("");
  const [notes, setNotes] = useState("");

  // Loading states
  const [loadingBOMs, setLoadingBOMs] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);

  // New state for URL parameter handling
  const [urlParamProcessed, setUrlParamProcessed] = useState(false);
  const [prefilledSalesOrder, setPrefilledSalesOrder] = useState(null);

  // Move handleSalesOrderToggle function definition here, before useEffects
  const handleSalesOrderToggle = useCallback(async (salesOrder) => {
    const isSelected = selectedSalesOrders.some(
      (so) => so._id === salesOrder._id
    );

    if (isSelected) {
      setSelectedSalesOrders((prev) =>
        prev.filter((so) => so._id !== salesOrder._id)
      );
    } else {
      // Load sales order items
      try {
        const items = await apiFetchSalesOrderProducts(salesOrder._id);
        const soWithItems = { ...salesOrder, items };
        setSelectedSalesOrders((prev) => [...prev, soWithItems]);
      } catch (error) {
        toast.error("Failed to load sales order items");
      }
    }
  }, [selectedSalesOrders]);

  const canNext = useMemo(() => {
    if (step === 0)
      return requestType === "SALES_ORDER" || requestType === "STOCK_FILLUP";
    if (step === 1) {
      if (requestType === "SALES_ORDER")
        return (
          selectedSalesOrders.length > 0 &&
          selectedProduct &&
          Number(quantityRequired) > 0
        );
      return !!selectedProductId && Number(quantityRequired) > 0;
    }
    if (step === 2) return !!selectedBOMId && calculatedRMs.length > 0;
    if (step === 3) return !!requiredDate;
    return true;
  }, [
    step,
    requestType,
    selectedSalesOrders,
    selectedProduct,
    selectedProductId,
    quantityRequired,
    selectedBOMId,
    calculatedRMs,
    requiredDate,
  ]);

  // Load initial data
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, u, so] = await Promise.all([
          apiFetchProducts(),
          apiFetchUsers(),
          apiFetchSalesOrders(),
        ]);
        setProducts(p);
        setUsers(u);
        setSalesOrders(so);
      } catch (error) {
        toast.error("Failed to load initial data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Updated useEffect to handle URL parameters properly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const salesOrderParam = urlParams.get('salesOrder');
    
    if (salesOrderParam && !urlParamProcessed && salesOrders.length > 0) {
      try {
        const salesOrderData = JSON.parse(decodeURIComponent(salesOrderParam));
        console.log('Pre-filled sales order data:', salesOrderData);
        
        // Find the sales order in the loaded data
        const salesOrder = salesOrders.find(so => so._id === salesOrderData.salesOrderId);
        
        if (salesOrder) {
          // Set request type to SALES_ORDER (this will already be set, but ensuring)
          setRequestType('SALES_ORDER');
          
          // Automatically select this sales order by calling the toggle function
          // This will fetch the items and set the selected sales order
          handleSalesOrderToggle(salesOrder);
          
          // Move to step 1 (source/product selection)
          setStep(1);
          
          setUrlParamProcessed(true);
        }
      } catch (error) {
        console.error('Error parsing sales order data:', error);
        toast.error('Error loading pre-filled sales order data');
      }
    }
  }, [salesOrders, urlParamProcessed, handleSalesOrderToggle]); // Add handleSalesOrderToggle to dependencies

  // Group products from selected sales orders
  useEffect(() => {
    if (selectedSalesOrders.length === 0) {
      setGroupedProducts([]);
      return;
    }

    const productMap = new Map();

    selectedSalesOrders.forEach((so) => {
      so.items?.forEach((item) => {
        const productId = item.product._id;
        const productKey = productId;

        if (productMap.has(productKey)) {
          const existing = productMap.get(productKey);
          existing.totalQuantity += item.quantity;
          existing.salesOrderDetails.push({
            salesOrderId: so._id,
            salesOrderNumber: so.orderNumber,
            salesOrderItemId: item._id,
            quantity: item.quantity,
            unit: item.unit || item.product?.unitId?.unitCode || "units",
            expectedDeliveryDate: so.EDD || so.expectedDeliveryDate,
            quality: item.quality,
          });
        } else {
          productMap.set(productKey, {
            product: item.product,
            totalQuantity: item.quantity,
            unit: item.unit || item.product?.unitId?.unitCode || "units",
            salesOrderDetails: [
              {
                salesOrderId: so._id,
                salesOrderNumber: so.orderNumber,
                salesOrderItemId: item._id,
                quantity: item.quantity,
                unit: item.unit || item.product?.unitId?.unitCode || "units",
                expectedDeliveryDate: so.EDD || so.expectedDeliveryDate,
                quality: item.quality,
              },
            ],
          });
        }
      });
    });

    const groupedProductsArray = Array.from(productMap.values());
    setGroupedProducts(groupedProductsArray);

    // Auto-select the first product if coming from URL parameter and no product is selected yet
    if (selectedSalesOrders.length === 1 && groupedProductsArray.length > 0 && !selectedProduct && !prefilledSalesOrder) {
      const firstProduct = groupedProductsArray[0];
      setSelectedProduct(firstProduct);
      setQuantityRequired(String(firstProduct.totalQuantity));
      setPrefilledSalesOrder(selectedSalesOrders[0]);
    }
  }, [selectedSalesOrders, selectedProduct, prefilledSalesOrder]);

  // When product changes → load BOMs
  useEffect(() => {
    const productId =
      requestType === "SALES_ORDER"
        ? selectedProduct?.product?._id
        : selectedProductId;
    if (!productId) return;

    (async () => {
      setLoadingBOMs(true);
      try {
        const list = await apiFetchBOMs(productId);
        setBoms(list);
      } catch (error) {
        toast.error("Failed to load BOMs");
      } finally {
        setLoadingBOMs(false);
      }
    })();
  }, [requestType, selectedProduct, selectedProductId]);

  // Compute RMs and check stock when BOM or quantity changes
  useEffect(() => {
    const q = Number(quantityRequired);
    const bom = boms.find((b) => b._id === selectedBOMId);
    setSelectedBOM(bom || null);

    if (!bom || !q || q <= 0) {
      setCalculatedRMs([]);
      setStockStatus([]);
      setHasLowStock(false);
      return;
    }

    const productId =
      requestType === "SALES_ORDER"
        ? selectedProduct?.product?._id
        : selectedProductId;

    const output = bom.outputs?.find((o) => o.productId?._id === productId);
    const outputPercentage = output?.percentage || 100;

    const rms = (bom.rawMaterials || []).map((rm) => {
      // Each batch uses rm.quantity (10kg) and produces outputPercentage% of that as the desired product
      // So each batch produces: (rm.quantity * outputPercentage / 100) of the desired product
      const productPerBatch = (rm.quantity || 0) * (outputPercentage / 100);

      // To get q kg of product, we need: q / productPerBatch batches
      const batchesNeeded = q / productPerBatch;

      // Total raw material needed: batchesNeeded * rm.quantity
      const requiredQuantity = batchesNeeded * (rm.quantity || 0);

      return {
        materialId: rm.materialId?._id || rm.materialId,
        materialName:
          rm.materialId?.productName || rm.materialName || "Material",
        requiredQuantity,
        unit: rm.unit || "kg",
      };
    });

    setCalculatedRMs(rms);

    // Check stock for each material
    checkMaterialStock(rms);
  }, [
    selectedBOMId,
    boms,
    quantityRequired,
    selectedProduct,
    selectedProductId,
    requestType,
  ]);

  // Set minimum required date based on earliest sales order delivery date
  useEffect(() => {
    if (requestType === "SALES_ORDER" && selectedSalesOrders.length > 0) {
      const validDates = selectedSalesOrders
        .map((so) => so.expectedDeliveryDate || so.EDD)
        .filter((date) => date && !isNaN(new Date(date).getTime()))
        .map((date) => new Date(date));

      if (validDates.length > 0) {
        const earliestDate = validDates.reduce((earliest, current) =>
          current < earliest ? current : earliest
        );

        const requiredByDate = new Date(earliestDate);
        requiredByDate.setDate(requiredByDate.getDate() - 1);
        setRequiredDate(requiredByDate.toISOString().split("T")[0]);
      }
    }
  }, [requestType, selectedSalesOrders]);

  const checkMaterialStock = async (materials) => {
    setLoadingStock(true);
    try {
      const stockPromises = materials.map(async (rm) => {
        const stock = await apiFetchMaterialStock(rm.materialId);
        const isLowStock = stock.availableQuantity < rm.requiredQuantity;
        return {
          ...rm,
          availableStock: stock.availableQuantity,
          isLowStock,
        };
      });

      const stockResults = await Promise.all(stockPromises);
      setStockStatus(stockResults);
      setHasLowStock(stockResults.some((item) => item.isLowStock));
    } catch (error) {
      toast.error("Failed to check stock levels");
    } finally {
      setLoadingStock(false);
    }
  };

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const productChosen = useMemo(() => {
    if (requestType === "SALES_ORDER")
      return selectedProduct?.product?._id || null;
    return selectedProductId || null;
  }, [requestType, selectedProduct, selectedProductId]);

  const productName = useMemo(() => {
    if (requestType === "SALES_ORDER")
      return selectedProduct?.product?.productName || "";
    const p = products.find((x) => x._id === selectedProductId);
    return p?.productName || "";
  }, [requestType, selectedProduct, selectedProductId, products]);

  async function submitRequest(createPurchaseRequest = false) {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const decoded = jwtDecode(token);
      const createdBy = decoded.userId;

      // Prepare sales orders data for multiple selection
      const salesOrdersData =
        requestType === "SALES_ORDER"
          ? selectedProduct?.salesOrderDetails?.map((detail) => ({
              salesOrderId: detail.salesOrderId,
              salesOrderItemId: detail.salesOrderItemId,
              quantityFromOrder: detail.quantity,
            })) || []
          : [];

      const payload = {
        requestedBy: createdBy,
        requestType,
        salesOrders: salesOrdersData,
        productId: productChosen,
        bomId: selectedBOMId,
        quantityRequired: Number(quantityRequired),
        assignedTo: assignedTo || null,
        requiredDate: requiredDate || null,
        notes,
        rawMaterials: calculatedRMs,
        createPurchaseRequest,
      };

      const res = await apiCreateProductionRequest(payload);
      if (res?.ok) {
        const message = createPurchaseRequest
          ? "Production request created with purchase request for low stock items!"
          : "Production request created successfully!";
        toast.success(message);
        resetForm();
      } else {
        toast.error(res?.error || "Failed to create request");
      }
    } catch (error) {
      toast.error("Failed to create production request");
    } finally {
      setLoading(false);
    }
  }

  const resetForm = () => {
    setStep(0);
    setRequestType("SALES_ORDER");
    setSelectedSalesOrders([]);
    setGroupedProducts([]);
    setSelectedProduct(null);
    setSelectedProductId("");
    setQuantityRequired("");
    setSelectedBOMId("");
    setCalculatedRMs([]);
    setStockStatus([]);
    setHasLowStock(false);
    setAssignedTo("");
    setRequiredDate("");
    setNotes("");
    setUrlParamProcessed(false);
    setPrefilledSalesOrder(null);
  };

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/production-requests">Production Requests</Link>
          </li>
          <li className="breadcrumb-item active">Create</li>
        </ol>
      </nav>
      <Row className="mb-3">
        <Col>
          <h2>Production Request</h2>
        </Col>
        <Col className="text-end"></Col>
      </Row>
      <Container fluid className="my-5">
        <Card className="shadow-sm">
          <Card.Header>
            <Stepper step={step} />
          </Card.Header>

          <Card.Body>
            {step === 0 && (
              <StepType
                requestType={requestType}
                setRequestType={setRequestType}
              />
            )}

            {step === 1 && (
              <StepSource
                requestType={requestType}
                salesOrders={salesOrders}
                selectedSalesOrders={selectedSalesOrders}
                groupedProducts={groupedProducts}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                products={products}
                selectedProductId={selectedProductId}
                setSelectedProductId={setSelectedProductId}
                quantityRequired={quantityRequired}
                setQuantityRequired={setQuantityRequired}
                prefilledSalesOrder={prefilledSalesOrder}
                handleSalesOrderToggle={handleSalesOrderToggle}
              />
            )}

            {step === 2 && (
              <StepBOM
                productName={productName}
                boms={boms}
                selectedBOMId={selectedBOMId}
                setSelectedBOMId={setSelectedBOMId}
                calculatedRMs={calculatedRMs}
                stockStatus={stockStatus}
                loadingBOMs={loadingBOMs}
                loadingStock={loadingStock}
              />
            )}

            {step === 3 && (
              <StepAssign
                users={users}
                assignedTo={assignedTo}
                setAssignedTo={setAssignedTo}
                requiredDate={requiredDate}
                setRequiredDate={setRequiredDate}
                notes={notes}
                setNotes={setNotes}
              />
            )}

            {step === 4 && (
              <StepReview
                requestType={requestType}
                productName={productName}
                quantityRequired={quantityRequired}
                selectedBOM={selectedBOM}
                calculatedRMs={calculatedRMs}
                stockStatus={stockStatus}
                assignedTo={assignedTo}
                users={users}
                requiredDate={requiredDate}
                notes={notes}
                selectedProduct={selectedProduct}
                hasLowStock={hasLowStock}
              />
            )}

            {/* Navigation Buttons */}
            <div className="mt-4 d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={goBack}
                disabled={step === 0 || loading}
              >
                <FaChevronLeft className="me-1" /> Back
              </Button>

              {step < 4 ? (
                <Button onClick={goNext} disabled={!canNext || loading}>
                  {loading ? <Spinner size="sm" className="me-1" /> : null}
                  Next <FaChevronRight className="ms-1" />
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    onClick={() => submitRequest(false)}
                    disabled={!canNext || loading}
                    variant="primary"
                  >
                    {loading ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <FaCheck className="me-1" />
                    )}
                    Submit
                  </Button>
                  {/* {hasLowStock && (
                    <Button
                      onClick={() => submitRequest(true)}
                      disabled={!canNext || loading}
                      variant="warning"
                    >
                      {loading ? (
                        <Spinner size="sm" className="me-1" />
                      ) : (
                        <FaShoppingCart className="me-1" />
                      )}
                      Submit + Purchase Request
                    </Button>
                  )} */}
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
}

export function Stepper({ step }) {
  return (
    <div className="row g-2">
      {STEPS.map((s, idx) => (
        <div
          key={s.key}
          className={`col m-1 d-flex align-items-center gap-2 border rounded p-3 ${
            idx <= step ? "border-primary" : "border-secondary"
          }`}
        >
          <div
            className={`rounded-circle p-2 d-flex align-items-center justify-content-center ${
              idx <= step
                ? "bg-primary bg-opacity-10"
                : "bg-secondary bg-opacity-10"
            }`}
          >
            {s.icon}
          </div>
          <span className={idx === step ? "fw-semibold" : "text-muted"}>
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

export function StepType({ requestType, setRequestType }) {
  return (
    <div className="mb-4">
      <p className="small text-muted">
        Choose how you want to generate the request.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <TypeCard
            title="Against Sales Order"
            description="Pick an SO, then choose its product & quantity (editable)."
            active={requestType === "SALES_ORDER"}
            onClick={() => setRequestType("SALES_ORDER")}
          />
        </div>
        <div className="col-md-6">
          <TypeCard
            title="Stock Fill-Up"
            description="Produce directly for inventory replenishment."
            active={requestType === "STOCK_FILLUP"}
            onClick={() => setRequestType("STOCK_FILLUP")}
          />
        </div>
      </div>
    </div>
  );
}

function TypeCard({ title, description, active, onClick }) {
  return (
    <Button
      onClick={onClick}
      variant={active ? "primary" : "transparent"}
      className={`w-100 text-start border ${
        active ? "border-primary" : ""
      } rounded-3 p-3`}
    >
      <div className="mb-2 fw-semibold fs-8">{title}</div>
      <div className="small">{description}</div>
    </Button>
  );
}

function StepSource(props) {
  const {
    requestType,
    salesOrders,
    selectedSalesOrders,
    groupedProducts,
    selectedProduct,
    setSelectedProduct,
    products,
    selectedProductId,
    setSelectedProductId,
    quantityRequired,
    setQuantityRequired,
    prefilledSalesOrder,
    handleSalesOrderToggle, // received from parent
  } = props;

  const handleProductSelect = (groupedProduct) => {
    setSelectedProduct(groupedProduct);
    // keep product id in parallel so equality checks are stable across re-creations
    setSelectedProductId(groupedProduct.product._id);
    setQuantityRequired(String(groupedProduct.totalQuantity));
  };

  const isSelectedProduct = (groupedProduct) =>
    !!selectedProduct && (selectedProduct.product?._id || selectedProductId) === groupedProduct.product._id;

  return (
    <div className="mb-4">
      {requestType === "SALES_ORDER" ? (
        <div className="mb-4">
          {/* Show prefilled sales order information if available */}
          {prefilledSalesOrder && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center">
                <FaCheck className="me-2 text-success" />
                <strong>Pre-selected Sales Order: {prefilledSalesOrder.orderNumber}</strong>
              </div>
              <small className="text-muted">
                This sales order was automatically selected from your previous action.
              </small>
            </Alert>
          )}

          {/* Multiple Sales Order Selection */}
          <div className="mb-3">
            <Form.Label>Select Sales Orders</Form.Label>
            <div
              className="border rounded p-3"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {salesOrders.map((so) => {
                const isSelected = selectedSalesOrders.some(
                  (selected) => selected._id === so._id
                );
                return (
                  <Form.Check
                    key={so._id}
                    type="checkbox"
                    id={`so-${so._id}`}
                    label={
                      <div>
                        <div className="fw-medium">{so.orderNumber}</div>
                        <div className="small text-muted">
                          Expected:{" "}
                          {so.EDD
                            ? new Date(so.EDD).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "Not specified"}
                        </div>
                      </div>
                    }
                    checked={isSelected}
                    onChange={() => handleSalesOrderToggle(so)}
                    className="mb-2"
                  />
                );
              })}
            </div>
          </div>

          {/* Grouped Products (no per-SO details table) */}
          {groupedProducts.length > 0 && (
            <div className="mb-3">
              <Form.Label>Select Product (Grouped by Same Items)</Form.Label>
              <div className="row g-2">
                {groupedProducts.map((groupedProduct, index) => (
                  <div className="col-md-12" key={index}>
                    <Card
                      className={`cursor-pointer ${
                        isSelectedProduct(groupedProduct)
                          ? "border-primary bg-light"
                          : ""
                      }`}
                      onClick={() => handleProductSelect(groupedProduct)}
                    >
                      <Card.Body className="p-3">
                        <Form.Check
                          type="radio"
                          name="groupedProduct"
                          checked={isSelectedProduct(groupedProduct)}
                          onChange={() => handleProductSelect(groupedProduct)}
                          label={
                            <div>
                              <div className="fw-medium text-primary fs-8 mb-2">
                                {groupedProduct.product.productName}
                                <Badge bg="primary" className="ms-2">
                                  Total: {groupedProduct.totalQuantity} {groupedProduct.unit}
                                </Badge>
                              </div>

                              {/* simplified: show brief list of source orders without table */}
                              <div className="mt-2 small text-muted">
                                From Orders:{" "}
                                {groupedProduct.salesOrderDetails
                                  .map((d) => d.salesOrderNumber)
                                  .join(", ")}
                              </div>
                            </div>
                          }
                        />
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quantity Input (single column — removed Total Order Value) */}
          {selectedProduct && (
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Label>Production Quantity Required</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    min={0}
                    max={selectedProduct.totalQuantity}
                    value={quantityRequired}
                    onChange={(e) => setQuantityRequired(e.target.value)}
                    placeholder="Enter required quantity"
                  />
                  <span className="input-group-text">
                    {selectedProduct.unit}
                  </span>
                </div>
                <small className="text-muted">
                  Max available from orders: {selectedProduct.totalQuantity} {selectedProduct.unit}
                </small>
              </div>
            </div>
          )}
        </div>
      ) : (
        // STOCK_FILLUP section unchanged
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Label>Product</Form.Label>
            <Form.Select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
            >
              <option value="">Select Product</option>
              {products.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.productName} ({p.unitId?.unitCode || "units"})
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-6">
            <Form.Label>Quantity Required</Form.Label>
            <div className="input-group">
              <Form.Control
                type="number"
                min={0}
                value={quantityRequired}
                onChange={(e) => setQuantityRequired(e.target.value)}
                placeholder="e.g., 200"
              />
              {selectedProductId && (
                <span className="input-group-text">
                  {products.find((p) => p._id === selectedProductId)?.unitId
                    ?.unitCode || "units"}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepBOM({
  productName,
  boms,
  selectedBOMId,
  setSelectedBOMId,
  calculatedRMs,
  stockStatus,
  loadingBOMs,
  loadingStock,
}) {
  const selectedBOM = boms.find((b) => b._id === selectedBOMId);

  return (
    <div className="mb-4">
      {/* Product Name */}
      <div className="mb-3">
        <Form.Label>Product</Form.Label>
        <div className="mt-1 fw-medium">{productName || "—"}</div>
      </div>

      {/* BOM Selection */}
      <div className="mb-3">
        <Form.Label>BOM Selection</Form.Label>
        {loadingBOMs ? (
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="me-2" />
            <span>Loading BOMs...</span>
          </div>
        ) : (
          <Form.Select
            value={selectedBOMId}
            onChange={(e) => setSelectedBOMId(e.target.value)}
          >
            <option value="">Select BOM</option>
            {boms.map((b) => {
              const yieldSummary = b.outputs
                ?.map((output) => {
                  const name =
                    output.productId?.productName ||
                    output.productId?.alias ||
                    output.productId?.productCode ||
                    "Unnamed";
                  return `${name}: ${output.percentage || 0}%`;
                })
                .join(", ");

              return (
                <option key={b._id} value={b._id} title={yieldSummary}>
                  {b.name || b.code || "BOM"} • Total Yield:{" "}
                  {b.outputs?.reduce((sum, o) => sum + (o.percentage || 0), 0)}%
                </option>
              );
            })}
          </Form.Select>
        )}
        <small className="text-muted">
          Raw materials auto-calculated from BOM yield.
        </small>
      </div>

      {/* Processing Stages */}
      {selectedBOM?.processingStages?.length > 0 && (
        <div className="border rounded mb-3">
          <div className="border-bottom p-2 fw-semibold small">
            Processing Stages
          </div>
          <div className="p-2 p-md-3 px-1 px-md-3 small">
            <div className="row fw-semibold mb-2">
              <div className="col-4">Stage Name</div>
              <div className="col-3">Gain/Loss</div>
              <div className="col-3">Capacity/Hour</div>
              <div className="col-2">Unit</div>
            </div>
            {selectedBOM.processingStages.map((stage, idx) => (
              <div className="row align-items-center" key={idx}>
                <div className="col-4">{stage.stageName}</div>
                <div className="col-3">
                  <Badge bg={stage.gainLoss >= 0 ? "success" : "danger"}>
                    {stage.gainLoss >= 0 ? "+" : ""}{stage.gainLoss}%
                  </Badge>
                </div>
                <div className="col-3">
                  {stage.capacityPerHour || "N/A"}
                </div>
                <div className="col-2">
                  {stage.capacityUnit || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calculated Raw Materials with Stock Status */}
      <div className="border rounded d-none">
        <div className="border-bottom p-2 fw-semibold small d-flex justify-content-between align-items-center">
          <span>Calculated Raw Materials</span>
          {loadingStock && (
            <div className="d-flex align-items-center">
              <Spinner size="sm" className="me-1" />
              <small className="text-muted">Checking stock...</small>
            </div>
          )}
        </div>
        <div className="p-2 p-md-3 px-1 px-md-3 small">
          {calculatedRMs?.length ? (
            <>
              <div className="row fw-semibold mb-2">
                <div className="col-5">Material</div>
                <div className="col-3">Required</div>
                <div className="col-2">Available</div>
                <div className="col-2">Status</div>
              </div>
              {stockStatus.length > 0
                ? stockStatus.map((rm, idx) => (
                    <div className="row align-items-center" key={idx}>
                      <div className="col-5">{rm.materialName}</div>
                      <div className="col-3">
                        {Number(rm.requiredQuantity).toFixed(2)} {rm.unit}
                      </div>
                      <div className="col-2">
                        {rm.availableStock?.toFixed(2) || "N/A"}
                      </div>
                      <div className="col-2">
                        {rm.isLowStock ? (
                          <Badge
                            bg="danger"
                            className="d-flex align-items-center"
                          >
                            <FaExclamationTriangle className="me-1" size={10} />
                            Low Stock
                          </Badge>
                        ) : (
                          <Badge bg="success">OK</Badge>
                        )}
                      </div>
                    </div>
                  ))
                : calculatedRMs.map((rm, idx) => (
                    <div className="row" key={idx}>
                      <div className="col-5">{rm.materialName}</div>
                      <div className="col-3">
                        {Number(rm.requiredQuantity).toFixed(2)} {rm.unit}
                      </div>
                      <div className="col-4 text-muted">Stock checking...</div>
                    </div>
                  ))}
            </>
          ) : (
            <div className="text-muted small">
              Select a BOM to see required materials.
            </div>
          )}
        </div>
      </div>

      {/* Low Stock Alert */}
      {/* {stockStatus.some((item) => item.isLowStock) && (
        <Alert variant="warning" className="mt-3">
          <FaExclamationTriangle className="me-2" />
          <strong>Low Stock Alert:</strong> Some materials have insufficient
          stock. You can create a purchase request along with the production
          request.
        </Alert>
      )} */}
    </div>
  );
}


function StepAssign({
  users,
  assignedTo,
  setAssignedTo,
  requiredDate,
  setRequiredDate,
  notes,
  setNotes,
}) {
  return (
    <div className="row g-3">
      {/* Assign to */}
      <div className="col-md-6">
        <Form.Label className="d-flex align-items-center gap-1">
          Assign to (optional)
        </Form.Label>
        <Form.Select
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
        >
          <option value="">Unassigned</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name || u.email}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Required Date */}
      <div className="col-md-6">
        <Form.Label className="d-flex align-items-center gap-1">
          Required Date <FaCalendarAlt size={14} />
        </Form.Label>
        <Form.Control
          type="date"
          value={requiredDate}
          onChange={(e) => setRequiredDate(e.target.value)}
          min={new Date().toISOString().split("T")[0]} // Restrict past dates
        />
      </div>

      {/* Notes */}
      <div className="col-12">
        <Form.Label>Notes</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes or instructions"
        />
      </div>
    </div>
  );
}

function StepReview({
  requestType,
  productName,
  quantityRequired,
  selectedBOM,
  calculatedRMs,
  stockStatus,
  assignedTo,
  users,
  requiredDate,
  notes,
  selectedProduct,
  hasLowStock,
}) {
  const assigneeName = useMemo(
    () => users.find((u) => u._id === assignedTo)?.name || "—",
    [users, assignedTo]
  );

  return (
    <div className="mb-4">
      {/* Summary Section */}
      <div className="border rounded mb-4">
        <div className="border-bottom p-2 fw-semibold small">Summary</div>
        <div className="row p-3 small">
          <div className="col-md-6">
            <SummaryRow
              label="Request Type"
              value={
                requestType === "SALES_ORDER"
                  ? "Against Sales Order"
                  : "Stock Fill-Up"
              }
            />
            <SummaryRow label="Product" value={productName || "—"} />
            <SummaryRow
              label="Quantity"
              value={
                quantityRequired
                  ? `${quantityRequired} ${selectedProduct?.unit || "units"}`
                  : "—"
              }
            />
            <SummaryRow
              label="BOM"
              value={
                selectedBOM
                  ? selectedBOM.name || selectedBOM.code || "BOM"
                  : "—"
              }
            />
          </div>
          <div className="col-md-6">
            <SummaryRow
              label="Yield"
              value={
                selectedBOM?.outputs
                  ? `${selectedBOM.outputs.reduce(
                      (sum, o) => sum + (o.percentage || 0),
                      0
                    )}% (${selectedBOM.outputs
                      .map((o) => {
                        const name =
                          o.productId?.productName ||
                          o.productId?.alias ||
                          o.productId?.productCode ||
                          "Unnamed";
                        return `${name}: ${o.percentage || 0}%`;
                      })
                      .join(", ")})`
                  : "100%"
              }
            />
            <SummaryRow label="Required Date" value={requiredDate || "—"} />
            <SummaryRow label="Assigned To" value={assigneeName} />
            <SummaryRow label="Notes" value={notes || "—"} />
          </div>
        </div>
      </div>

      {/* Sales Orders Summary */}
      {requestType === "SALES_ORDER" && selectedProduct && (
        <div className="border rounded mb-4">
          <div className="border-bottom p-2 fw-semibold small">
            Sales Orders
          </div>
          <div className="p-3 small">
            <div className="row fw-semibold mb-2">
              <div className="col-4">Sales Order</div>
              <div className="col-4">Quantity</div>
              <div className="col-4">Delivery Date</div>
            </div>
            {selectedProduct.salesOrderDetails.map((detail, idx) => (
              <div key={idx} className="row mb-1">
                <div className="col-4">{detail.salesOrderNumber}</div>
                <div className="col-4">
                  {detail.quantity} {detail.unit}
                </div>
                <div className="col-4">
                  {new Date(detail.expectedDeliveryDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw Materials with Stock Status */}
      <div className="border rounded d-none">
        <div className="border-bottom p-2 fw-semibold small d-flex justify-content-between align-items-center">
          <span>Raw Materials & Stock Status</span>
          {hasLowStock && (
            <Badge bg="warning">
              <FaExclamationTriangle className="me-1" />
              Low Stock Items
            </Badge>
          )}
        </div>
        <div className="p-2 p-md-3 px-1 px-md-3 small">
          {stockStatus?.length ? (
            <>
              <div className="row fw-semibold mb-2">
                <div className="col-5">Material</div>
                <div className="col-3">Required</div>
                <div className="col-2">Available</div>
                <div className="col-2">Status</div>
              </div>
              {stockStatus.map((rm, idx) => (
                <div className="row align-items-center" key={idx}>
                  <div className="col-5">{rm.materialName}</div>
                  <div className="col-3">
                    {Number(rm.requiredQuantity).toFixed(2)} {rm.unit}
                  </div>
                  <div className="col-2">
                    {rm.availableStock?.toFixed(2) || "N/A"}
                  </div>
                  <div className="col-2">
                    {rm.isLowStock ? (
                      <Badge bg="danger">Low Stock</Badge>
                    ) : (
                      <Badge bg="success">OK</Badge>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : calculatedRMs?.length ? (
            <>
              <div className="row fw-semibold mb-2">
                <div className="col-7">Material</div>
                <div className="col-5">Required Qty</div>
              </div>
              {calculatedRMs.map((rm, idx) => (
                <div className="row" key={idx}>
                  <div className="col-7">{rm.materialName}</div>
                  <div className="col-5">
                    {Number(rm.requiredQuantity).toFixed(2)} {rm.unit}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="text-muted small">No materials calculated.</div>
          )}
        </div>
      </div>

      {/* Final Alert for Low Stock */}
      {/* {hasLowStock && (
        <Alert variant="info" className="mt-3">
          <FaExclamationTriangle className="me-2" />
          <strong>Action Required:</strong> Some materials have low stock. You
          can:
          <ul className="mb-0 mt-2">
            <li>
              <strong>Submit:</strong> Create production request only
            </li>
            <li>
              <strong>Submit + Purchase Request:</strong> Create production
              request and purchase request for low stock items
            </li>
          </ul>
        </Alert>
      )} */}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="row mb-2">
      <div className="col-5 text-muted">{label}</div>
      <div className="col-7 fw-medium">{value}</div>
    </div>
  );
}

/***************************
 * Placeholder API methods *
 ***************************/

async function apiFetchProducts() {
  try {
    const res = await fetch(
      `${config.API_BASE_URL}/products?populateUnit=true`
    );
    if (!res.ok) throw new Error("Failed to fetch products");
    return await res.json();
  } catch (e) {
    console.error(e);
    return [];
  }
}

async function apiFetchUsers() {
  try {
    const res = await fetch(`${config.API_BASE_URL}/staff`);
    if (!res.ok) throw new Error("Failed to fetch users");
    return await res.json();
  } catch (e) {
    console.error(e);
    return [];
  }
}

async function apiFetchSalesOrders() {
  try {
    const res = await fetch(`${config.API_BASE_URL}/sales-orders/new`);
    if (!res.ok) throw new Error("Failed to fetch sales orders");
    return await res.json();
  } catch (e) {
    console.error(e);
    return [];
  }
}

async function apiFetchSalesOrderProducts(soId) {
  try {
    const res = await fetch(
      `${config.API_BASE_URL}/sales-order-items?salesOrderId=${soId}`
    );
    if (!res.ok) throw new Error("Failed to fetch sales order products");
    return await res.json();
  } catch (e) {
    console.error(e);
    return [];
  }
}

async function apiFetchBOMs(productId) {
  try {
    const res = await fetch(
      `${config.API_BASE_URL}/process-definitions/outputs?productId=${productId}`
    );
    if (!res.ok) throw new Error("Failed to fetch BOMs");
    return await res.json();
  } catch (e) {
    console.error(e);
    return [];
  }
}

async function apiCreateProductionRequest(payload) {
  try {
    const res = await fetch(`${config.API_BASE_URL}/production-requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Failed to create production request");

    const data = await res.json();
    return { ok: true, data };
  } catch (e) {
    console.error(e);
    return { ok: false, error: e?.message || "Network error" };
  }
}

async function apiFetchMaterialStock(materialId) {
  try {
    const res = await fetch(
      `${config.API_BASE_URL}/inventory/stock/${materialId}`
    );
    if (!res.ok) throw new Error("Failed to fetch material stock");
    return await res.json();
  } catch (e) {
    console.error(e);
    return { availableQuantity: 0 };
  }
}
